import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useMemo, useSyncExternalStore } from 'react';
import type { tDraftMessages } from './MessageBar.types';
import type { tChatAttachment } from '../../Chat.types';
import { releaseBlobUrlHolder, setBlobUrlHolder } from 'src/utils/blobUrlRegistry';

const STORAGE_KEY = 'draftMessages';
const SYNC_STORAGE_EVENT = 'AHOOKS_SYNC_STORAGE_EVENT_NAME';
const listeners = new Set<() => void>();
const draftBlobHolder = {};
let snapshot: tDraftMessages[] = [];
let draftsByLead = new Map<string, tDraftMessages>();
let rawValue: string | null | undefined;
let initialized = false;
let hasUnpersistedWrite = false;

const omitFileFromStorage = (key: string, value: unknown): unknown => key === 'file' ? undefined : value;

const cloneDraft = (draft: tDraftMessages): tDraftMessages => {
  const copy = { ...draft };
  if (draft.attachments) copy.attachments = draft.attachments.map((attachment) => ({ ...attachment }));
  return copy;
};

const isDraft = (value: unknown): value is tDraftMessages => {
  if (!value || typeof value !== 'object') return false;
  const draft = value as Record<string, unknown>;
  return typeof draft.leadId === 'string' && typeof draft.message === 'string' &&
    (draft.attachments === undefined || (Array.isArray(draft.attachments) &&
      draft.attachments.every((attachment) => attachment !== null && typeof attachment === 'object')));
};

const publish = (next: tDraftMessages[], raw: string | null) => {
  let changed = next.length !== snapshot.length;
  const entries = next.map((draft, index) => {
    const previous = draftsByLead.get(draft.leadId);
    const entry = previous && isEqual(previous, draft) ? previous : cloneDraft(draft);
    if (entry !== snapshot[index]) changed = true;
    return entry;
  });
  rawValue = raw;
  initialized = true;
  if (!changed) return;
  setBlobUrlHolder(draftBlobHolder, entries.flatMap((draft) => draft.attachments?.map((attachment) => attachment.src) ?? []));
  snapshot = entries;
  draftsByLead = new Map();
  for (const draft of entries) {
    if (!draftsByLead.has(draft.leadId)) draftsByLead.set(draft.leadId, draft);
  }
  listeners.forEach((listener) => listener());
};

const restoreLocalFiles = (draft: tDraftMessages): tDraftMessages => {
  const previous = draftsByLead.get(draft.leadId);
  if (!previous || !draft.attachments) return draft;
  return { ...draft, attachments: draft.attachments.map((attachment) => {
    if (!attachment.src?.startsWith('blob:')) return attachment;
    const file = previous.attachments?.find((entry) => entry.src === attachment.src)?.file;
    return file ? { ...attachment, file } : attachment;
  }) };
};

const refreshFromStorage = () => {
  if (typeof window === 'undefined' || hasUnpersistedWrite) return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (initialized && raw === rawValue) return;
    let next: unknown = [];
    try { next = raw ? JSON.parse(raw, omitFileFromStorage) : []; } catch { /* Corrupt storage is an empty draft list. */ }
    publish(Array.isArray(next) ? next.filter(isDraft).map(restoreLocalFiles) : [], raw);
  } catch {
    // A storage read failure must retain the in-memory draft, including after a failed write.
  }
};

const onStorageChange = (event: Event) => {
  const change: { key?: string | null; storageArea?: Storage | null } =
    event instanceof CustomEvent ? event.detail : event as StorageEvent;
  if (change?.key !== STORAGE_KEY && change?.key !== null) return;
  try {
    if (change.storageArea && change.storageArea !== window.localStorage) return;
  } catch { return; }
  hasUnpersistedWrite = false;
  refreshFromStorage();
};

export const subscribeDraftMessages = (listener: () => void): (() => void) => {
  if (listeners.size === 0 && typeof window !== 'undefined') {
    refreshFromStorage();
    window.addEventListener('storage', onStorageChange);
    window.addEventListener(SYNC_STORAGE_EVENT, onStorageChange);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorageChange);
      window.removeEventListener(SYNC_STORAGE_EVENT, onStorageChange);
    }
  };
};

export const getDraftMessagesSnapshot = (): tDraftMessages[] => {
  if (!initialized || listeners.size === 0) refreshFromStorage();
  return snapshot;
};

export const setDraftMessages: Dispatch<SetStateAction<tDraftMessages[] | undefined>> = (update) => {
  const current = getDraftMessagesSnapshot().map(cloneDraft);
  const value = typeof update === 'function' ? update(current) : update;
  const next = value ?? [];
  const raw = value === undefined ? null : JSON.stringify(value, omitFileFromStorage);
  // Publish a detached snapshot first: legacy composers mutate their working array in place.
  publish(next, raw);
  try {
    if (typeof window === 'undefined') return;
    if (raw === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, raw);
    hasUnpersistedWrite = false;
    window.dispatchEvent(new CustomEvent(SYNC_STORAGE_EVENT, {
      detail: { key: STORAGE_KEY, newValue: raw, storageArea: window.localStorage },
    }));
  } catch {
    hasUnpersistedWrite = true;
  }
};

export const useDraftMessage = (leadId: string): tDraftMessages | undefined => {
  const getSnapshot = useCallback(() => {
    getDraftMessagesSnapshot();
    return draftsByLead.get(leadId);
  }, [leadId]);
  return useSyncExternalStore(subscribeDraftMessages, getSnapshot, getSnapshot);
};

export const retainDraftBlobAttachments = (leadId: string | undefined, attachments: tChatAttachment[]) => {
  const local = attachments.filter((attachment) => attachment.src?.startsWith('blob:'));
  if (!leadId || !local.length) return;
  setDraftMessages((drafts) => {
    const next = drafts ?? [];
    const draft = next.find((entry) => entry.leadId === leadId);
    if (!draft) return [...next, { leadId, message: '', attachments: local }];
    const existing = new Set(draft.attachments?.map((attachment) => attachment.src));
    draft.attachments = [...(draft.attachments ?? []), ...local.filter((attachment) => !existing.has(attachment.src))];
    return next;
  });
};

type tAttachmentSend = { attempted: tChatAttachment[]; failed: tChatAttachment[] };

export const remainingAttachmentsAfterSend = (current: tChatAttachment[], result: tAttachmentSend): tChatAttachment[] => {
  const attempted = new Set(result.attempted.map((attachment) => attachment.src));
  return [...result.failed, ...current.filter((attachment) => !attempted.has(attachment.src))];
};

export const settleDraftAttachmentSend = (drafts: tDraftMessages[] | undefined, result: tAttachmentSend & { leadId: string | undefined; message: string }): tDraftMessages[] => {
  const current = drafts ?? [];
  if (!result.leadId) return current;
  const existing = current.find((draft) => draft.leadId === result.leadId);
  const attachments = remainingAttachmentsAfterSend(existing?.attachments ?? [], result);
  const currentMessage = existing?.message ?? '';
  const message = currentMessage.trim() === result.message.trim() ? '' : currentMessage;
  if (!attachments.length && !message) return current.filter((draft) => draft.leadId !== result.leadId);
  const updated = { leadId: result.leadId, message, attachments };
  return existing ? current.map((draft) => draft === existing ? updated : draft) : [...current, updated];
};

export const useDraftMessagesState = (): [tDraftMessages[], typeof setDraftMessages] => {
  const drafts = useSyncExternalStore(subscribeDraftMessages, getDraftMessagesSnapshot, getDraftMessagesSnapshot);
  const workingCopy = useMemo(() => drafts.map(cloneDraft), [drafts]);
  return [workingCopy, setDraftMessages];
};

export const resetDraftMessagesForTests = () => {
  releaseBlobUrlHolder(draftBlobHolder);
  snapshot = [];
  draftsByLead = new Map();
  rawValue = undefined;
  initialized = false;
  hasUnpersistedWrite = false;
};
